import React, {useEffect, useState} from 'react';
import {
    Alert,
    AppBar,
    Avatar,
    Backdrop,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Container,
    Divider,
    Drawer,
    Fade,
    Grid,
    IconButton,
    InputAdornment,
    Link,
    List,
    ListItem,
    ListItemText,
    Modal,
    Snackbar,
    Stack,
    Tab,
    Tabs,
    TextField,
    Toolbar,
    Typography,
    useScrollTrigger,
    CircularProgress
} from '@mui/material';
import School from "./schools/School";
import UserManualModal from "./UserManualModal";
import {
    AccessTime as TimeIcon,
    CheckCircle,
    Close as CloseIcon,
    Cloud,
    Email as EmailIcon,
    Facebook,
    GetApp as GetAppIcon,
    Instagram,
    LinkedIn,
    LocationOn as LocationIcon,
    Menu as MenuIcon,
    Phone as PhoneIcon,
    PhoneIphone,
    Psychology,
    Public,
    Schedule,
    School as SchoolIcon,
    Security,
    Send as SendIcon,
    Speed,
    Support,
    TrendingUp,
    Twitter,
    Visibility,
    VisibilityOff,
} from '@mui/icons-material';
import logo from '../Images/logo-1.png';
import {useDispatch} from 'react-redux';
import {keyframes} from '@mui/system';
import {fetchSchoolById} from "./schools/redux/schoolActions";
import {fetchUserFailure, fetchUserSuccess} from "../page/dashboard/redux/userActions";
import {api, LOGINAPI} from "../../common";
import {setToken, setUser} from "./redux/userSlice";
// Animation keyframes
const fadeInUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const float = keyframes`
    0%, 100% {
        transform: translateY(0px);
    }
    50% {
        transform: translateY(-10px);
    }
`;

const pulse = keyframes`
    0% {
        box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.7);
    }
    70% {
        box-shadow: 0 0 0 10px rgba(25, 118, 210, 0);
    }
    100% {
        box-shadow: 0 0 0 0 rgba(25, 118, 210, 0);
    }
`;

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: {xs: '95%', sm: 600, md: 700},
    bgcolor: 'background.paper',
    borderRadius: 4,
    boxShadow: '0 25px 50px rgba(0,0,0,0.15)',
    p: 4,
    backdropFilter: 'blur(10px)',
};


const FeatureCard = ({icon: IconComponent, title, description, delay = 0}) => (
    <Fade in timeout={1000} style={{transitionDelay: `${delay}ms`}}>
        <Card
            sx={{
                height: '100%',
                textAlign: 'center',
                p: 3,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                border: '1px solid rgba(0,0,0,0.05)',
                borderRadius: 3,
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
                    '& .feature-icon': {
                        transform: 'scale(1.1) rotate(5deg)',
                    },
                    '&::before': {
                        opacity: 1,
                    }
                },
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                }
            }}
        >
            <CardContent>
                <Avatar
                    className="feature-icon"
                    sx={{
                        bgcolor: 'transparent',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        mx: 'auto',
                        mb: 3,
                        width: 70,
                        height: 70,
                        transition: 'all 0.3s ease',
                        animation: `${float} 3s ease-in-out infinite`,
                        animationDelay: `${delay * 0.001}s`,
                    }}
                >
                    <IconComponent fontSize="large" sx={{color: 'white'}}/>
                </Avatar>
                <Typography variant="h5" component="h4" gutterBottom sx={{fontWeight: 700, color: '#1a202c'}}>
                    {title}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{lineHeight: 1.7}}>
                    {description}
                </Typography>
            </CardContent>
        </Card>
    </Fade>
);

const PricingCard = ({title, price, features, featured = false, delay = 0}) => (
    <Fade in timeout={1000} style={{transitionDelay: `${delay}ms`}}>
        <Card
            sx={{
                height: '100%',
                border: featured ? '2px solid' : '1px solid',
                borderColor: featured ? 'primary.main' : 'rgba(0,0,0,0.08)',
                position: 'relative',
                borderRadius: 3,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                background: featured
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                color: featured ? 'white' : 'inherit',
                overflow: 'hidden',
                '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: featured
                        ? '0 25px 50px rgba(102, 126, 234, 0.4)'
                        : '0 20px 40px rgba(0,0,0,0.12)',
                }
            }}
        >
            {featured && (
                <Chip
                    label="MOST POPULAR"
                    sx={{
                        position: 'absolute',
                        top: -12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        bgcolor: '#ff6b6b',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.75rem',
                        animation: `${pulse} 2s infinite`,
                    }}
                />
            )}
            <CardHeader
                title={title}
                subheader={`₹${price}/year`}
                titleTypographyProps={{
                    align: 'center',
                    variant: 'h4',
                    fontWeight: 700,
                    color: featured ? 'white' : '#1a202c'
                }}
                subheaderTypographyProps={{
                    align: 'center',
                    variant: 'h5',
                    fontWeight: 600,
                    color: featured ? 'rgba(255,255,255,0.9)' : 'primary.main'
                }}
                sx={{pb: 2}}
            />
            <CardContent sx={{pt: 0}}>
                <List dense>
                    {features.map((feature, index) => (
                        <ListItem key={index} sx={{py: 0.5, px: 0}}>
                            <CheckCircle
                                sx={{
                                    mr: 1,
                                    fontSize: '1.2rem',
                                    color: featured ? 'rgba(255,255,255,0.9)' : 'success.main'
                                }}
                            />
                            <ListItemText
                                primary={feature}
                                sx={{
                                    '& .MuiTypography-root': {
                                        fontSize: '0.95rem',
                                        color: featured ? 'rgba(255,255,255,0.95)' : 'text.primary'
                                    }
                                }}
                            />
                        </ListItem>
                    ))}
                </List>
                <Box sx={{mt: 3, textAlign: 'center'}}>
                    <Button
                        variant={featured ? 'contained' : 'outlined'}
                        size="large"
                        fullWidth
                        sx={{
                            py: 1.5,
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            borderRadius: 3,
                            textTransform: 'none',
                            ...(featured ? {
                                bgcolor: 'rgba(255,255,255,0.2)',
                                color: 'white',
                                border: '2px solid rgba(255,255,255,0.3)',
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.3)',
                                    transform: 'translateY(-2px)',
                                }
                            } : {
                                borderColor: 'primary.main',
                                color: 'primary.main',
                                '&:hover': {
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    transform: 'translateY(-2px)',
                                }
                            })
                        }}
                    >
                        Choose Plan
                    </Button>
                </Box>
            </CardContent>
        </Card>
    </Fade>
);

// Scroll-triggered navbar
const ElevationScroll = ({children}) => {
    const trigger = useScrollTrigger({
        disableHysteresis: true,
        threshold: 0,
    });

    return React.cloneElement(children, {
        elevation: trigger ? 4 : 0,
        style: {
            backgroundColor: trigger ? 'rgba(255,255,255,0.95)' : 'transparent',
            backdropFilter: trigger ? 'blur(10px)' : 'none',
            transition: 'all 0.3s ease',
        },
    });
};

export default function LandingPage({onBooleanChange}) {
    const [open, setOpen] = useState(false);
    const [openForgetPassword, setOpenForgetPassword] = useState(false);
    const [phone, setPhone] = useState('');
    const [otpPassword, setOtpPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [invalidCred, setInvalidCred] = useState('');
    const [tabIndex, setTabIndex] = useState(0);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: '',
    });
    const [loading, setLoading] = useState(false);
    const [loginLoading, setLoginLoading] = useState(false);
    const [openUserManual, setOpenUserManual] = useState(false);

    const dispatch = useDispatch();

    const handleOpenModal = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const handleOpenForgetPassword = () => {
        setOpen(false);
        setOpenForgetPassword(true);
    };

    const handleCloseForgetPassword = () => {
        setOpenForgetPassword(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setInvalidCred('');
        setLoginLoading(true);

        if (!phone && !otpPassword) {
            setInvalidCred('Please Enter Email Address And Password');
            setLoginLoading(false);
            return;
        } else if (!phone) {
            setInvalidCred('Please Enter Email Address');
            setLoginLoading(false);
            return;
        } else if (!otpPassword) {
            setInvalidCred('Please Enter Password');
            setLoginLoading(false);
            return;
        }

        const LOGIN_PASSWORD = "/auth/login";
        try {
            const responseIsEmail = await LOGINAPI.get(`/api/master/isUserExist/${phone}`);
            const emailExists = responseIsEmail.data === true;

            if (emailExists) {
                const response = await LOGINAPI.post(LOGIN_PASSWORD, {email: phone, password: otpPassword});
                if (response.status === 200) {
                    const token = response.data.token;
                    dispatch(setUser(response.data.loginId));
                    dispatch(setToken(token));
                    // Store in localStorage
                    localStorage.setItem('token', token);

// Store in sessionStorage
                    sessionStorage.setItem('token', token);

// Store in cookies (expires in 7 days for example)
                    document.cookie = `token=${token}; path=/; max-age=${7 * 24 * 60 * 60}; secure; samesite=strict`;


                    if (response.data.loginId) {
                        try {
                            const responseUser = await api.get(`/api/user/${response.data.loginId}`);
                            dispatch(fetchUserSuccess(responseUser.data));
                            if (responseUser.data) {
                                dispatch(fetchSchoolById(responseUser.data.schoolId));
                                handleClose();
                                onBooleanChange();
                            }
                        } catch (error) {
                            dispatch(fetchUserFailure(error.message));
                        } finally {
                            setLoginLoading(false);
                        }
                    }
                } else {
                    setInvalidCred(response.data.response || 'Invalid credentials');
                    setLoginLoading(false);
                }
            } else {
                setInvalidCred("EmailId does not exist in our records. Please check the emailId and try again.");
                setLoginLoading(false);
            }
        } catch (error) {
            console.error('Error:', error);
            if (error.response?.data?.status === 401) {
                setInvalidCred(error.response.data.description);
            } else if (error.response?.data?.status === 403) {
                setInvalidCred(error.response.data.description);
            } else if (error.response?.data?.status === 500) {
                setInvalidCred(error.response.data.description);
            } else {
                setInvalidCred("An error occurred. Please try again later.");
            }
            setLoginLoading(false);
        }
    };

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleChange = (e) => {
        const {name, value} = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
        setInvalidCred('');
    };

    const handleRegistrationSuccess = ({email, password}) => {
        setPhone(email);
        setOtpPassword(password);
        setTabIndex(0);
        setOpen(true);
        setSnackbarOpen(true);
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

  const handleContactSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);
      try {
          // Add timestamp to the message data
          const dataWithTimestamp = {
              ...formData,
              timestamp: new Date().toISOString()
          };

          const response = await api.post('/api/contact/send-message', dataWithTimestamp);
          setFormData({name: '', email: '', subject: '', message: ''});
          setSnackbarOpen(true);
      } catch (error) {
          console.error('Error sending message:', error);
      } finally {
          setLoading(false);
      }
  };

    // Auto-rotate hero slides
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % slides.length);
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    const slides = [
        {
            title: "Welcome to Our School",
            subtitle: "School Management System",
            description: "Empowering students to achieve their potential through innovative education.",
            gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            action: "Get Started"
        },
        {
            title: "Explore Our Programs",
            subtitle: "Learn more about what we offer",
            description: "Innovative curriculum tailored to nurture young minds and build future leaders.",
            gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
            action: "Learn More"
        },
        {
            title: "Join Our Community",
            subtitle: "Be a part of our vibrant community",
            description: "Connect, grow, and thrive together in an environment of excellence.",
            gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
            action: "Sign Up"
        }
    ];

    const features = [
        {
            icon: Public,
            title: "Global Reach",
            description: "Expand your horizons with our global reach capabilities. Manage operations seamlessly across borders and enjoy smooth international transactions."
        },
        {
            icon: Cloud,
            title: "Cloud Infrastructure",
            description: "Our robust cloud infrastructure ensures your data is secure and easily accessible. Benefit from high availability and disaster recovery features."
        },
        {
            icon: Support,
            title: "24/7 Support",
            description: "Our dedicated support team is here to assist you around the clock. From troubleshooting to guidance, we're always here to help you succeed."
        },
        {
            icon: Schedule,
            title: "Flexible Scheduling",
            description: "We offer flexible scheduling options to fit your needs and accommodate different learning styles and preferences."
        },
        {
            icon: SchoolIcon,
            title: "Expert Teachers",
            description: "Our educators are experts in their fields with a passion for teaching and nurturing young minds to reach their full potential."
        },
        {
            icon: Psychology,
            title: "Modern Education",
            description: "Innovative approaches to education that foster learning, creativity, and critical thinking in the digital age."
        }
    ];

    const pricingPlans = [
        {
            title: "Basic",
            price: "15,999",
            features: [
                "Up to 500 Students",
                "Student & Staff Management",
                "Enquiry Management",
                "Attendance (Student & Staff)",
                "Fee Management & Reports",
                "Exam & Marksheet",
                "Notifications System",
                "Gallery & Events",
                "Certificate Generation",
                "Basic Reports",
                "Web Portal Access",
                "Mobile App (Android)",
                "Cloud Storage (15 GB)",
                "Email Support (24 hrs)"
            ]
        },
        {
            title: "Professional",
            price: "29,999",
            featured: true,
            features: [
                "Up to 2000 Students",
                "Everything in Basic",
                "Library Management",
                "Transport & Bus Tracking",
                "Timetable Management",
                "Assignment & Daily Activities",
                "Study Material Distribution",
                "Staff Salary Management",
                "Expense Management",
                "Asset Management",
                "Communication (Mail & Chat)",
                "Online Meeting Integration",
                "MCQ/Online Test Module",
                "Advanced Reports & Analytics",
                "SMS Integration (Configurable)",
                "Polls & Surveys",
                "Mobile App (iOS & Android)",
                "Cloud Storage (50 GB)",
                "Priority Support",
                "Free Training (3 Sessions)"
            ]
        },
        {
            title: "Enterprise",
            price: "59,999",
            features: [
                "Unlimited Students",
                "Everything in Professional",
                "AI-Powered Chat Assistant",
                "Multi-Campus Support",
                "Scholarship Management",
                "Complaint Management",
                "Help & Support Portal",
                "Advanced Leave Management",
                "Master Data Management",
                "Promotion & Transfer",
                "Grade & Syllabus Management",
                "Class Teacher Assignment",
                "Custom Certificates",
                "Comprehensive Report Suite",
                "WhatsApp Integration",
                "API Access & Integration",
                "Custom Development (Limited)",
                "Cloud Storage (200 GB)",
                "Dedicated Account Manager",
                "24/7 Priority Support",
                "On-site Training & Setup",
                "Custom Reports & Dashboards"
            ]
        }
    ];

    const navigationItems = [
        {label: 'Home', href: '#home'},
        {label: 'About', href: '#about'},
        {label: 'Features', href: '#features'},
        {label: 'Pricing', href: '#pricing'},
        {label: 'Contact', href: '#contact'},
        {label: 'User Manual', onClick: () => setOpenUserManual(true)}
    ];

    return (
        <>
            {/* User Manual Modal */}
            <UserManualModal open={openUserManual} onClose={() => setOpenUserManual(false)} />

            {/* Login/Registration Modal */}
            <Modal
                open={open}
                onClose={handleClose}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                    sx: {backdropFilter: 'blur(5px)'}
                }}
            >
                <Fade in={open}>
                    <Box sx={modalStyle}>
                        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3}}>
                            <Typography variant="h4" sx={{fontWeight: 700, color: '#1a202c'}}>
                                {tabIndex === 0 ? 'Welcome Back' : 'Join Our School'}
                            </Typography>
                            <IconButton
                                onClick={handleClose}
                                sx={{
                                    '&:hover': {
                                        bgcolor: 'rgba(0,0,0,0.05)',
                                        transform: 'rotate(90deg)',
                                    },
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                <CloseIcon/>
                            </IconButton>
                        </Box>

                        {invalidCred && (
                            <Alert
                                severity="error"
                                sx={{
                                    width: '100%',
                                    mb: 3,
                                    borderRadius: 2,
                                    '& .MuiAlert-message': {
                                        fontSize: '0.95rem'
                                    }
                                }}
                            >
                                {invalidCred}
                            </Alert>
                        )}

                        <Tabs
                            value={tabIndex}
                            onChange={handleTabChange}
                            centered
                            sx={{
                                '& .MuiTab-root': {
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    minWidth: 120,
                                }
                            }}
                        >
                            <Tab label="Sign In"/>
                            <Tab label="Register"/>
                        </Tabs>

                        {tabIndex === 0 && (
                            <Box component="form" onSubmit={handleSubmit} sx={{mt: 4}}>
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    label="Email Address"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    autoFocus
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                        }
                                    }}
                                />
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    label="Password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={otpPassword}
                                    onChange={(e) => setOtpPassword(e.target.value)}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                        }
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={handleClickShowPassword}
                                                    edge="end"
                                                    sx={{'&:hover': {bgcolor: 'rgba(0,0,0,0.05)'}}}
                                                >
                                                    {showPassword ? <VisibilityOff/> : <Visibility/>}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    disabled={loginLoading}
                                    sx={{
                                        mt: 3,
                                        mb: 2,
                                        py: 1.5,
                                        fontSize: '1.1rem',
                                        fontWeight: 600,
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        background: loginLoading
                                            ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
                                            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        '&:hover': {
                                            background: loginLoading
                                                ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
                                                : 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                                            transform: loginLoading ? 'none' : 'translateY(-1px)',
                                        },
                                        '&.Mui-disabled': {
                                            color: 'rgba(255, 255, 255, 0.7)',
                                        },
                                        transition: 'all 0.2s ease',
                                        position: 'relative',
                                    }}
                                >
                                    {loginLoading ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <CircularProgress
                                                size={20}
                                                sx={{
                                                    color: 'rgba(255, 255, 255, 0.9)',
                                                }}
                                            />
                                            <span>Signing In...</span>
                                        </Box>
                                    ) : (
                                        'Sign In'
                                    )}
                                </Button>
                                <Button
                                    fullWidth
                                    variant="text"
                                    onClick={handleOpenForgetPassword}
                                    sx={{textTransform: 'none'}}
                                >
                                    Forgot Password?
                                </Button>
                            </Box>
                        )}

                        {tabIndex === 1 && (
                            <Box sx={{mt: 3}}>
                                <School onRegistrationSuccess={handleRegistrationSuccess}/>
                            </Box>
                        )}
                    </Box>
                </Fade>
            </Modal>

            {/* Success Snackbar */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{vertical: 'top', horizontal: 'center'}}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity="success"
                    sx={{width: '100%', borderRadius: 2}}
                >
                    <strong>Success!</strong> Your message has been sent successfully.
                </Alert>
            </Snackbar>

            {/* Navigation */}
            <ElevationScroll>
                <AppBar position="fixed" sx={{zIndex: 1300}}>
                    <Toolbar sx={{py: 1}}>
                        <Box sx={{display: 'flex', alignItems: 'center', flexGrow: 1}}>
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mr: 2,
                                }}
                            >
                                <img src={logo} alt="Logo" style={{height: 40, marginRight: 16}}/>
                            </Box>
                            <Typography variant="h6" sx={{fontWeight: 700, color: 'text.primary'}}>
                                ScholiQ
                            </Typography>
                        </Box>

                        <Box sx={{display: {xs: 'none', md: 'flex'}, gap: 3, alignItems: 'center'}}>
                            {navigationItems.map((item) =>
                                item.onClick ? (
                                    <Typography
                                        key={item.label}
                                        onClick={item.onClick}
                                        sx={{
                                            color: 'text.primary',
                                            fontWeight: 500,
                                            cursor: 'pointer',
                                            transition: 'color 0.2s ease',
                                            '&:hover': {
                                                color: 'primary.main',
                                            }
                                        }}
                                    >
                                        {item.label}
                                    </Typography>
                                ) : (
                                    <Link
                                        key={item.label}
                                        href={item.href}
                                        underline="none"
                                        sx={{
                                            color: 'text.primary',
                                            fontWeight: 500,
                                            transition: 'color 0.2s ease',
                                            '&:hover': {
                                                color: 'primary.main',
                                            }
                                        }}
                                    >
                                        {item.label}
                                    </Link>
                                )
                            )}
                            <Button
                                variant="contained"
                                onClick={handleOpenModal}
                                sx={{
                                    borderRadius: 3,
                                    px: 3,
                                    py: 1,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                                        transform: 'translateY(-1px)',
                                    },
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                Get Started
                            </Button>
                        </Box>

                        {/* Mobile Navigation */}
                        <IconButton
                            sx={{display: {xs: 'block', md: 'none'}}}
                            onClick={() => setDrawerOpen(true)}
                        >
                            <MenuIcon/>
                        </IconButton>
                    </Toolbar>
                </AppBar>
            </ElevationScroll>

            <Drawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                PaperProps={{
                    sx: {
                        width: 280,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                    }
                }}
            >
                <Box sx={{pt: 3, pb: 2}}>
                    <Typography variant="h6" sx={{px: 2, fontWeight: 700, mb: 2}}>
                        ScholiQ
                    </Typography>
                    <List>
                        {navigationItems.map((item) => (
                            <ListItem
                                key={item.label}
                                component="a"
                                href={item.href}
                                onClick={() => setDrawerOpen(false)}
                                sx={{
                                    '&:hover': {
                                        bgcolor: 'rgba(255,255,255,0.1)',
                                    }
                                }}
                            >
                                <ListItemText
                                    primary={item.label}
                                    primaryTypographyProps={{fontWeight: 500}}
                                />
                            </ListItem>
                        ))}
                        <ListItem sx={{pt: 2}}>
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={() => {
                                    setDrawerOpen(false);
                                    handleOpenModal();
                                }}
                                sx={{
                                    bgcolor: 'rgba(255,255,255,0.2)',
                                    color: 'white',
                                    '&:hover': {
                                        bgcolor: 'rgba(255,255,255,0.3)',
                                    }
                                }}
                            >
                                Get Started
                            </Button>
                        </ListItem>
                    </List>
                </Box>
            </Drawer>

            {/* Hero Section */}
            <Box
                id="home"
                sx={{
                    pt: 10,
                    minHeight: '100vh',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    background: slides[currentSlide]?.gradient || slides[0].gradient,
                    transition: 'all 1s ease-in-out',
                    overflow: 'hidden',
                }}
            >
                <Container>
                    <Grid container alignItems="center" spacing={4}>
                        <Grid item xs={12} md={6}>
                            <Box sx={{color: 'white', animation: `${fadeInUp} 1s ease-out`}}>
                                <Typography
                                    variant="h2"
                                    component="h1"
                                    gutterBottom
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: {xs: '2.5rem', md: '3.5rem'},
                                        lineHeight: 1.2,
                                        mb: 2,
                                    }}
                                >
                                    {slides[currentSlide]?.title}
                                </Typography>
                                <Typography
                                    variant="h5"
                                    gutterBottom
                                    sx={{
                                        fontWeight: 600,
                                        mb: 2,
                                        opacity: 0.95,
                                    }}
                                >
                                    {slides[currentSlide]?.subtitle}
                                </Typography>
                                <Typography
                                    variant="h6"
                                    paragraph
                                    sx={{
                                        mb: 4,
                                        lineHeight: 1.6,
                                        opacity: 0.9,
                                    }}
                                >
                                    {slides[currentSlide]?.description}
                                </Typography>
                                <Stack direction="row" spacing={2}>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={handleOpenModal}
                                        sx={{
                                            px: 4,
                                            py: 1.5,
                                            fontSize: '1.1rem',
                                            fontWeight: 600,
                                            borderRadius: 3,
                                            textTransform: 'none',
                                            bgcolor: 'rgba(255,255,255,0.2)',
                                            border: '2px solid rgba(255,255,255,0.3)',
                                            backdropFilter: 'blur(10px)',
                                            '&:hover': {
                                                bgcolor: 'rgba(255,255,255,0.3)',
                                                transform: 'translateY(-2px)',
                                            },
                                            transition: 'all 0.3s ease',
                                        }}
                                    >
                                        {slides[currentSlide]?.action}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="large"
                                        sx={{
                                            px: 4,
                                            py: 1.5,
                                            fontSize: '1.1rem',
                                            fontWeight: 600,
                                            borderRadius: 3,
                                            textTransform: 'none',
                                            color: 'white',
                                            borderColor: 'rgba(255,255,255,0.5)',
                                            '&:hover': {
                                                borderColor: 'white',
                                                bgcolor: 'rgba(255,255,255,0.1)',
                                                transform: 'translateY(-2px)',
                                            },
                                            transition: 'all 0.3s ease',
                                        }}
                                    >
                                        Learn More
                                    </Button>
                                </Stack>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box
                                sx={{
                                    position: 'relative',
                                    textAlign: 'center',
                                    animation: `${fadeInUp} 1s ease-out 0.3s both`,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: {xs: 300, md: 400},
                                        height: {xs: 300, md: 400},
                                        mx: 'auto',
                                        borderRadius: '50%',
                                        background: 'rgba(255,255,255,0.1)',
                                        backdropFilter: 'blur(20px)',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        position: 'relative',
                                        animation: `${float} 6s ease-in-out infinite`,
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            inset: -20,
                                            borderRadius: '50%',
                                            border: '2px solid rgba(255,255,255,0.1)',
                                            animation: `${float} 4s ease-in-out infinite reverse`,
                                        },
                                        '&::after': {
                                            content: '""',
                                            position: 'absolute',
                                            inset: -40,
                                            borderRadius: '50%',
                                            border: '1px solid rgba(255,255,255,0.05)',
                                            animation: `${float} 8s ease-in-out infinite`,
                                        },
                                    }}
                                >
                                    <SchoolIcon
                                        sx={{
                                            fontSize: {xs: 120, md: 160},
                                            color: 'rgba(255,255,255,0.8)',
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>

                {/* Slide Indicators */}
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 40,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        gap: 2,
                    }}
                >
                    {slides.map((_, index) => (
                        <Box
                            key={index}
                            onClick={() => setCurrentSlide(index)}
                            sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                bgcolor: currentSlide === index ? 'white' : 'rgba(255,255,255,0.5)',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    bgcolor: 'white',
                                    transform: 'scale(1.2)',
                                }
                            }}
                        />
                    ))}
                </Box>
            </Box>

            {/* About Section */}
            <Box id="about" sx={{py: 12, bgcolor: '#f8fafc'}}>
                <Container>
                    <Box sx={{textAlign: 'center', mb: 8}}>
                        <Typography
                            variant="h3"
                            component="h2"
                            gutterBottom
                            sx={{
                                fontWeight: 800,
                                color: '#1a202c',
                                mb: 3,
                                animation: `${fadeInUp} 1s ease-out`,
                            }}
                        >
                            About Our System
                        </Typography>
                        <Typography
                            variant="h6"
                            color="text.secondary"
                            sx={{
                                maxWidth: 600,
                                mx: 'auto',
                                lineHeight: 1.7,
                                animation: `${fadeInUp} 1s ease-out 0.2s both`,
                            }}
                        >
                            Our comprehensive school management system is designed to streamline educational processes,
                            enhance communication, and improve overall efficiency in educational institutions.
                        </Typography>
                    </Box>

                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <Box sx={{animation: `${fadeInUp} 1s ease-out 0.4s both`}}>
                                <Typography
                                    variant="h4"
                                    gutterBottom
                                    sx={{fontWeight: 700, color: '#1a202c', mb: 3}}
                                >
                                    Why Choose ScholiQ?
                                </Typography>
                                <Typography variant="body1" paragraph sx={{lineHeight: 1.8, mb: 3}}>
                                    ScholiQ is more than just a management platform – it's a comprehensive solution
                                    that brings together students, teachers, parents, and administrators in one unified
                                    ecosystem. Our platform is built with modern technology and designed with user
                                    experience in mind.
                                </Typography>
                                <Stack spacing={2}>
                                    {[
                                        {icon: Security, text: 'Bank-grade security and data protection'},
                                        {icon: Speed, text: 'Lightning-fast performance and reliability'},
                                        {icon: TrendingUp, text: 'Proven track record of success'},
                                        {icon: Support, text: 'World-class customer support'}
                                    ].map((item, index) => (
                                        <Box key={index} sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                                            <Avatar
                                                sx={{
                                                    bgcolor: 'primary.main',
                                                    width: 40,
                                                    height: 40,
                                                }}
                                            >
                                                <item.icon fontSize="small"/>
                                            </Avatar>
                                            <Typography variant="body1" sx={{fontWeight: 500}}>
                                                {item.text}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box
                                sx={{
                                    animation: `${fadeInUp} 1s ease-out 0.6s both`,
                                    textAlign: 'center',
                                }}
                            >
                                <Box
                                    sx={{
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        borderRadius: 4,
                                        p: 6,
                                        color: 'white',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: -50,
                                            right: -50,
                                            width: 100,
                                            height: 100,
                                            borderRadius: '50%',
                                            background: 'rgba(255,255,255,0.1)',
                                        }
                                    }}
                                >
                                    <Typography variant="h2" sx={{fontWeight: 800, mb: 2}}>
                                        500+
                                    </Typography>
                                    <Typography variant="h6" sx={{mb: 3, opacity: 0.9}}>
                                        Schools Trust ScholiQ
                                    </Typography>
                                    <Typography variant="body1" sx={{opacity: 0.8}}>
                                        Join thousands of educational institutions that have transformed
                                        their operations with our comprehensive platform.
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Features Section */}
            <Box id="features" sx={{py: 12}}>
                <Container>
                    <Box sx={{textAlign: 'center', mb: 8}}>
                        <Typography
                            variant="h3"
                            component="h2"
                            gutterBottom
                            sx={{
                                fontWeight: 800,
                                color: '#1a202c',
                                mb: 3,
                                animation: `${fadeInUp} 1s ease-out`,
                            }}
                        >
                            Powerful Features
                        </Typography>
                        <Typography
                            variant="h6"
                            color="text.secondary"
                            sx={{
                                maxWidth: 600,
                                mx: 'auto',
                                lineHeight: 1.7,
                                animation: `${fadeInUp} 1s ease-out 0.2s both`,
                            }}
                        >
                            Discover the comprehensive features that make ScholiQ the perfect choice
                            for modern educational institutions.
                        </Typography>
                    </Box>

                    <Grid container spacing={4}>
                        {features.map((feature, index) => (
                            <Grid item xs={12} md={4} key={index}>
                                <FeatureCard
                                    icon={feature.icon}
                                    title={feature.title}
                                    description={feature.description}
                                    delay={index * 200}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Pricing Section */}
            <Box id="pricing" sx={{py: 12, bgcolor: '#f8fafc'}}>
                <Container>
                    <Box sx={{textAlign: 'center', mb: 8}}>
                        <Typography
                            variant="h3"
                            component="h2"
                            gutterBottom
                            sx={{
                                fontWeight: 800,
                                color: '#1a202c',
                                mb: 3,
                                animation: `${fadeInUp} 1s ease-out`,
                            }}
                        >
                            Choose Your Plan
                        </Typography>
                        <Typography
                            variant="h6"
                            color="text.secondary"
                            sx={{
                                maxWidth: 700,
                                mx: 'auto',
                                lineHeight: 1.7,
                                animation: `${fadeInUp} 1s ease-out 0.2s both`,
                            }}
                        >
                            Select the perfect plan for your institution. Transparent annual pricing with no hidden fees.
                            All plans include free updates, cloud hosting, and data security. Start your 30-day free trial today!
                        </Typography>
                    </Box>

                    <Grid container spacing={4}>
                        {pricingPlans.map((plan, index) => (
                            <Grid item xs={12} md={4} key={index}>
                                <PricingCard
                                    title={plan.title}
                                    price={plan.price}
                                    features={plan.features}
                                    featured={plan.featured}
                                    delay={index * 200}
                                />
                            </Grid>
                        ))}
                    </Grid>

                    <Box sx={{textAlign: 'center', mt: 6}}>
                        <Typography variant="body1" color="text.secondary" sx={{mb: 1}}>
                            <strong>Need a custom solution for multiple campuses or special requirements?</strong>
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
                            Get personalized pricing, volume discounts, and dedicated implementation support.
                        </Typography>
                        <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} justifyContent="center">
                            <Button
                                variant="outlined"
                                size="large"
                                sx={{
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: 3,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '1.1rem',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                    }
                                }}
                            >
                                Contact Sales
                            </Button>
                            <Button
                                variant="text"
                                size="large"
                                sx={{
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: 3,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '1.1rem',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                    }
                                }}
                            >
                                Schedule a Demo
                            </Button>
                        </Stack>
                    </Box>
                </Container>
            </Box>

            {/* Contact Section */}
            <Box id="contact" sx={{py: 12}}>
                <Container>
                    <Box sx={{textAlign: 'center', mb: 8}}>
                        <Typography
                            variant="h3"
                            component="h2"
                            gutterBottom
                            sx={{
                                fontWeight: 800,
                                color: '#1a202c',
                                mb: 3,
                                animation: `${fadeInUp} 1s ease-out`,
                            }}
                        >
                            Get in Touch
                        </Typography>
                        <Typography
                            variant="h6"
                            color="text.secondary"
                            sx={{
                                maxWidth: 600,
                                mx: 'auto',
                                lineHeight: 1.7,
                                animation: `${fadeInUp} 1s ease-out 0.2s both`,
                            }}
                        >
                            Ready to transform your educational institution? Contact us today for a
                            personalized demo and consultation.
                        </Typography>
                    </Box>

                    <Grid container spacing={6}>
                        <Grid item xs={12} md={8}>
                            <Card
                                sx={{
                                    p: 4,
                                    borderRadius: 3,
                                    boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                                    animation: `${fadeInUp} 1s ease-out 0.4s both`,
                                }}
                            >
                                <Typography variant="h5" gutterBottom sx={{fontWeight: 700, mb: 3}}>
                                    Send us a Message
                                </Typography>
                                <Box component="form" onSubmit={handleContactSubmit}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Your Name"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                required
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2,
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Email Address"
                                                name="email"
                                                type="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                required
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2,
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Subject"
                                                name="subject"
                                                value={formData.subject}
                                                onChange={handleChange}
                                                required
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2,
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Message"
                                                name="message"
                                                multiline
                                                rows={4}
                                                value={formData.message}
                                                onChange={handleChange}
                                                required
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2,
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                size="large"
                                                fullWidth
                                                startIcon={loading ? <CircularProgress size={20} color="inherit"/> : <SendIcon/>}
                                                sx={{
                                                    py: 1.5,
                                                    fontSize: '1.1rem',
                                                    fontWeight: 600,
                                                    borderRadius: 2,
                                                    textTransform: 'none',
                                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                    '&:hover': {
                                                        background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                                                        transform: 'translateY(-2px)',
                                                    },
                                                    transition: 'all 0.3s ease',
                                                }}
                                                disabled={loading}
                                            >
                                                {loading ? 'Sending...' : 'Send Message'}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box sx={{animation: `${fadeInUp} 1s ease-out 0.6s both`}}>
                                <Card
                                    sx={{
                                        p: 4,
                                        borderRadius: 3,
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        color: 'white',
                                        mb: 3,
                                    }}
                                >
                                    <Typography variant="h5" gutterBottom sx={{fontWeight: 700, mb: 3}}>
                                        Contact Information
                                    </Typography>
                                    <Stack spacing={3}>
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                                            <EmailIcon/>
                                            <Box>
                                                <Typography variant="body2" sx={{opacity: 0.8}}>
                                                    Email
                                                </Typography>
                                                <Typography variant="body1" sx={{fontWeight: 500}}>
                                                    info@ScholiQ.com
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                                            <PhoneIcon/>
                                            <Box>
                                                <Typography variant="body2" sx={{opacity: 0.8}}>
                                                    Phone
                                                </Typography>
                                                <Typography variant="body1" sx={{fontWeight: 500}}>
                                                    +91 79790 56554
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                                            <LocationIcon/>
                                            <Box>
                                                <Typography variant="body2" sx={{opacity: 0.8}}>
                                                    Address
                                                </Typography>
                                                <Typography variant="body1" sx={{fontWeight: 500}}>
                                                   New Town Rajarhat, Kolkata, IN
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                                            <TimeIcon/>
                                            <Box>
                                                <Typography variant="body2" sx={{opacity: 0.8}}>
                                                    Business Hours
                                                </Typography>
                                                <Typography variant="body1" sx={{fontWeight: 500}}>
                                                    Mon - Fri: 9:00 AM - 6:00 PM
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Stack>
                                </Card>

                                <Box sx={{textAlign: 'center'}}>
                                    <Typography variant="h6" gutterBottom sx={{fontWeight: 600}}>
                                        Follow Us
                                    </Typography>
                                    <Stack direction="row" spacing={2} justifyContent="center">
                                        {[Facebook, Twitter, LinkedIn, Instagram].map((Icon, index) => (
                                            <IconButton
                                                key={index}
                                                sx={{
                                                    bgcolor: 'primary.main',
                                                    color: 'white',
                                                    '&:hover': {
                                                        bgcolor: 'primary.dark',
                                                        transform: 'translateY(-2px)',
                                                    },
                                                    transition: 'all 0.3s ease',
                                                }}
                                            >
                                                <Icon/>
                                            </IconButton>
                                        ))}
                                    </Stack>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Mobile App Download Section */}
            <Box sx={{py: 12, bgcolor: '#f8fafc'}}>
                <Container>
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Box sx={{animation: `${fadeInUp} 1s ease-out`}}>
                                <Typography
                                    variant="h3"
                                    component="h2"
                                    gutterBottom
                                    sx={{
                                        fontWeight: 800,
                                        color: '#1a202c',
                                        mb: 3,
                                    }}
                                >
                                    Get Our Mobile App
                                </Typography>
                                <Typography
                                    variant="h6"
                                    color="text.secondary"
                                    sx={{
                                        lineHeight: 1.7,
                                        mb: 4,
                                    }}
                                >
                                    Access ScholiQ on the go! Download our mobile app to manage your school anytime,
                                    anywhere.
                                </Typography>
                                <Stack spacing={3}>
                                    <Box>
                                        <Typography variant="body1" sx={{mb: 2, fontWeight: 500}}>
                                            <GetAppIcon sx={{mr: 1, verticalAlign: 'middle'}}/>
                                            Key Features:
                                        </Typography>
                                        <Grid container spacing={2}>
                                            {[
                                                'Real-time notifications',
                                                'Student attendance tracking',
                                                'Fee payment',
                                                'Communication with teachers',
                                                'Access to study materials',
                                                'Event calendar'
                                            ].map((feature, index) => (
                                                <Grid item xs={12} sm={6} key={index}>
                                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                                        <CheckCircle sx={{color: 'success.main', fontSize: '1.2rem'}}/>
                                                        <Typography variant="body1">{feature}</Typography>
                                                    </Box>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>

                                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                                        <Button
                                            component="a"
                                            href="https://play.google.com/store/apps/details?id=com.scholiq"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            variant="contained"
                                            size="large"
                                            sx={{
                                                py: 1.5,
                                                px: 4,
                                                bgcolor: '#000',
                                                color: 'white',
                                                borderRadius: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                '&:hover': {
                                                    bgcolor: '#333',
                                                    transform: 'translateY(-2px)',
                                                },
                                                transition: 'all 0.3s ease',
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src="https://upload.wikimedia.org/wikipedia/commons/thumb/7/78/Google_Play_Store_badge_EN.svg/2560px-Google_Play_Store_badge_EN.svg.png"
                                                alt="Get it on Google Play"
                                                sx={{height: 40}}
                                            />
                                        </Button>
                                        <Button
                                            component="a"
                                            href="https://apps.apple.com/app/your-app-id"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            variant="contained"
                                            size="large"
                                            sx={{
                                                py: 1.5,
                                                px: 4,
                                                bgcolor: '#000',
                                                color: 'white',
                                                borderRadius: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                '&:hover': {
                                                    bgcolor: '#333',
                                                    transform: 'translateY(-2px)',
                                                },
                                                transition: 'all 0.3s ease',
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src="https://upload.wikimedia.org/wikipedia/commons/thumb/3/3c/Download_on_the_App_Store_Badge.svg/2560px-Download_on_the_App_Store_Badge.svg.png"
                                                alt="Download on the App Store"
                                                sx={{height: 40}}
                                            />
                                        </Button>
                                    </Stack>
                                </Stack>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box
                                sx={{
                                    position: 'relative',
                                    animation: `${float} 6s ease-in-out infinite`,
                                    textAlign: 'center',
                                }}
                            >
                                <Box
                                    sx={{
                                        position: 'relative',
                                        width: {xs: '280px', md: '320px'},
                                        height: {xs: '560px', md: '640px'},
                                        mx: 'auto',
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            inset: '-20px -60px',
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            borderRadius: '60px',
                                            transform: 'rotate(-6deg)',
                                            zIndex: 0,
                                        }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            width: '100%',
                                            height: '100%',
                                            background: '#fff',
                                            borderRadius: '40px',
                                            overflow: 'hidden',
                                            border: '8px solid #000',
                                            boxShadow: '0 25px 50px rgba(0,0,0,0.2)',
                                            zIndex: 1,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexDirection: 'column',
                                                color: 'white',
                                                p: 4,
                                                textAlign: 'center',
                                            }}
                                        >
                                            <PhoneIphone sx={{fontSize: 80, mb: 2}}/>
                                            <Typography variant="h5" sx={{fontWeight: 700, mb: 2}}>
                                                ScholiQ Mobile
                                            </Typography>
                                            <Typography variant="body1" sx={{opacity: 0.9}}>
                                                Your school management companion
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Footer */}
            <Box component="footer" sx={{py: 6, bgcolor: '#1a202c', color: 'white'}}>
                <Container>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={4}>
                            <Box sx={{display: 'flex', alignItems: 'center', mb: 3}}>
                                <Box
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: 2,
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mr: 2,
                                    }}
                                >
                                    <SchoolIcon sx={{color: 'white', fontSize: 24}}/>
                                </Box>
                                <Typography variant="h6" sx={{fontWeight: 700}}>
                                    ScholiQ
                                </Typography>
                            </Box>
                            <Typography variant="body1" sx={{opacity: 0.8, lineHeight: 1.7}}>
                                Transforming education through innovative technology solutions.
                                Empowering schools to achieve excellence in the digital age.
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <Typography variant="h6" gutterBottom sx={{fontWeight: 600}}>
                                Quick Links
                            </Typography>
                            <Stack spacing={1}>
                                {navigationItems.map((item) => (
                                    <Link
                                        key={item.label}
                                        href={item.href}
                                        color="inherit"
                                        underline="none"
                                        sx={{
                                            opacity: 0.8,
                                            '&:hover': {
                                                opacity: 1,
                                                color: 'primary.main',
                                            },
                                            transition: 'all 0.2s ease',
                                        }}
                                    >
                                        {item.label}
                                    </Link>
                                ))}
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Typography variant="h6" gutterBottom sx={{fontWeight: 600}}>
                                Services
                            </Typography>
                            <Stack spacing={1}>
                                {['Student Management', 'Staff Management', 'Fee Management', 'Attendance System', 'Report Generation'].map((service) => (
                                    <Typography key={service} variant="body2" sx={{opacity: 0.8}}>
                                        {service}
                                    </Typography>
                                ))}
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Typography variant="h6" gutterBottom sx={{fontWeight: 600}}>
                                Support
                            </Typography>
                            <Stack spacing={1}>
                                {['Help Center', 'Documentation', 'Training', 'API Reference', 'System Status'].map((support) => (
                                    <Typography key={support} variant="body2" sx={{opacity: 0.8}}>
                                        {support}
                                    </Typography>
                                ))}
                            </Stack>
                        </Grid>
                    </Grid>
                    <Divider sx={{my: 4, borderColor: 'rgba(255,255,255,0.1)'}}/>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: {xs: 'column', sm: 'row'},
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 2,
                        }}
                    >
                        <Typography variant="body2" sx={{opacity: 0.8}}>
                            © 2024 ScholiQ. All rights reserved.
                        </Typography>
                        <Stack direction="row" spacing={3}>
                            {['Privacy Policy', 'Terms of Service', 'Cookie Policy'].map((policy) => (
                                <Link
                                    key={policy}
                                    href="#"
                                    color="inherit"
                                    underline="none"
                                    variant="body2"
                                    sx={{
                                        opacity: 0.8,
                                        '&:hover': {
                                            opacity: 1,
                                            color: 'primary.main',
                                        },
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    {policy}
                                </Link>
                            ))}
                        </Stack>
                    </Box>
                </Container>
            </Box>
        </>
    );
}
